import { useState } from 'react';
import { apiRequest } from '../utils/api';

/**
 * Custom hook for managing tutorial system functionality
 * Handles tutorial state, creation, localStorage, and avatar journey
 */
const useTutorials = ({ messages, setMessages }) => {
  // 🎓 Interactive Tutorial System state
  const [isTutorialModeActive, setIsTutorialModeActive] = useState(false);
  const [activeTutorialData, setActiveTutorialData] = useState(null);
  const [tutorialTransitionState, setTutorialTransitionState] = useState('idle'); // idle -> entering -> active
  const [isAvatarJourneyActive, setIsAvatarJourneyActive] = useState(false);

  // Tutorial localStorage utilities
  const loadTutorialFromStorage = () => {
    try {
      const stored = localStorage.getItem('queryburst_last_tutorial');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed;
      }
    } catch (error) {
      console.warn('🎓 [useTutorials] Failed to load tutorial from localStorage:', error);
    }
    return null;
  };

  const reopenLastTutorial = () => {
    const storedTutorial = loadTutorialFromStorage();
    if (storedTutorial && storedTutorial.data) {
      handleTutorialStart(storedTutorial.data);
    }
  };

  // Tutorial handlers
  const handleTutorialStart = (tutorialData) => {
    setTutorialTransitionState('entering');
    setActiveTutorialData(tutorialData);
    
    // 💾 Save tutorial to localStorage for development persistence
    try {
      localStorage.setItem('queryburst_last_tutorial', JSON.stringify({
        data: tutorialData,
        timestamp: new Date().toISOString(),
        title: tutorialData?.title || tutorialData?.data?.tutorial_title || 'Untitled Tutorial'
      }));
    } catch (error) {
      console.warn('🎓 [useTutorials] Failed to save tutorial to localStorage:', error);
    }
    
    // Start avatar journey animation
    setIsAvatarJourneyActive(true);
  };

  // Handle avatar journey completion
  const handleAvatarJourneyComplete = () => {
    setIsAvatarJourneyActive(false);
    setIsTutorialModeActive(true);
    setTutorialTransitionState('active');
    
    // Add a small delay to ensure state has updated
    setTimeout(() => {
    }, 100);
  };
  
  const handleTutorialClose = () => {
    setIsTutorialModeActive(false);
    setActiveTutorialData(null);
    setTutorialTransitionState('idle');
    setIsAvatarJourneyActive(false);
  };

  // Transform direct tutorial content from agent into InteractiveTutorial format
  const handleDirectTutorialCreation = (tutorial_title, tutorial_content, metadata) => {
    // Parse the markdown content into sections by H2 headers
    const sections = [];
    const content = tutorial_content || '';
    
    // Split by H2 headers and process each section
    const parts = content.split(/^## /m);
    
    if (parts.length > 1) {
      // Process each H2 section (skip the first part which is usually H1 + intro)
      for (let i = 1; i < parts.length; i++) {
        const part = parts[i].trim();
        const lines = part.split('\n');
        const title = lines[0]; // First line is the H2 title
        const sectionContent = lines.slice(1).join('\n').trim(); // Rest is content
        
        if (title && sectionContent) {
          sections.push({
            id: `section_${i}`,
            title: title,
            content: sectionContent,
            updated: false,
            lastModified: new Date()
          });
        }
      }
    }
    
    // Fallback: If no H2 sections found, create one main section
    if (sections.length === 0) {
      const cleanContent = content.replace(/^#\s+[^\n]+\n*/m, '').trim();
      sections.push({
        id: 'main_content',
        title: 'Tutorial Content',
        content: cleanContent,
        updated: false,
        lastModified: new Date()
      });
    }
    
    // Create tutorialData object that InteractiveTutorial expects
    const tutorialData = {
      id: `tutorial_${Date.now()}`,
      title: tutorial_title,
      description: `Generated tutorial based on your audit findings and implementation request`,
      difficulty: metadata?.implementation_context?.priority === 'high' ? 'Advanced' : 
                  metadata?.implementation_context?.priority === 'low' ? 'Beginner' : 'Intermediate',
      estimatedTime: '30-45 minutes', // Will be estimated by backend
      context: {
        platform: 'WordPress', // Could be dynamic from business intel
        domain: window.location.hostname,
        tutorial_type: metadata?.implementation_context?.task_type || 'SEO Enhancement'
      },
      content: tutorial_content,
      tutorial_content: tutorial_content, // Ensure both fields are available
      
      // Pass through all agent data for task creation
      audit_findings: metadata?.audit_findings,
      implementation_context: metadata?.implementation_context,
      rationale: metadata?.rationale,
      
      metadata: metadata,
      sections: sections,
      word_count: metadata?.word_count || 0,
      priority: metadata?.implementation_context?.priority || 'medium',
      created_at: new Date().toISOString()
    };
    
    return tutorialData;
  };

  // Tutorial creation handler
  const handleTutorialCreation = async (card) => {
    try {
      // Prepare the payload structure
      const payload = {
        tutorial_data: {
          id: card.id,
          title: card.title,
          description: card.description,
          difficulty: card.difficulty,
          estimated_time: card.estimated_time,
          quick_win: card.quick_win,
          tutorial_context: card.tutorial_context
        },
        page_context: {
          current_url: window.location.href,
          current_path: window.location.pathname
        }
      };

      // Add loading message
      const loadingMessage = {
        id: messages.length + 1,
        type: 'agent',
        content: `🎓 **Creating Tutorial: "${card.title}"**\n\nI'm preparing your personalized tutorial...\n\n*Analyzing audit data and business context...*`,
        timestamp: new Date(),
        isLoading: true
      };
      setMessages(prev => [...prev, loadingMessage]);
      
      // Call the tutorial creation API using proper API utility
      const response = await apiRequest('/api/tutorials/create/', {
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' }
      });

      if (response.ok) {
        const result = await response.json();
        
        // Update the loading message with success
        setMessages(prev => prev.map(msg => 
          msg.isLoading ? {
            ...msg,
            content: `✅ **Tutorial Ready: "${result.tutorial_title}"**\n\nPerfect! Your personalized tutorial has been generated.\n\n**Opening Interactive Tutorial...**`,
            isLoading: false,
            isTutorialSuccess: true
          } : msg
        ));
        
        // Simple approach: Parse content by H2 sections, similar to working mock tutorials
        const sections = [];
        const content = result.tutorial_content || '';
        
        // Split by H2 headers and process each section
        const parts = content.split(/^## /m);
        
        if (parts.length > 1) {
          // Process each H2 section (skip the first part which is usually H1 + intro)
          for (let i = 1; i < parts.length; i++) {
            const part = parts[i].trim();
            const lines = part.split('\n');
            const title = lines[0]; // First line is the H2 title
            const sectionContent = lines.slice(1).join('\n').trim(); // Rest is content
            
            if (title && sectionContent) {
              sections.push({
                id: `section_${i}`,
                title: title,
                content: sectionContent,
                updated: false,
                lastModified: new Date()
              });
            }
          }
        }
        
        // Fallback: If no H2 sections found, create one main section
        if (sections.length === 0) {
          const cleanContent = content.replace(/^#\s+[^\n]+\n*/m, '').trim();
          sections.push({
            id: 'main_content',
            title: 'Tutorial Content',
            content: cleanContent,
            updated: false,
            lastModified: new Date()
          });
        }
        
        const tutorialData = {
          id: `tutorial_${Date.now()}`,
          title: result.tutorial_title,
          description: `A comprehensive guide tailored specifically to your website and business context`,
          difficulty: result.metadata?.difficulty || 'Intermediate',
          estimatedTime: result.metadata?.estimated_time || '30-45 minutes',
          context: {
            platform: 'WordPress', // Could be dynamic from business intel
            domain: window.location.hostname,
            tutorial_type: 'SEO Enhancement'
          },
          content: result.tutorial_content,
          metadata: result.metadata,
          sections: sections, // Use the simplified sections array
          word_count: result.metadata?.word_count || 0,
          priority: result.metadata?.priority || 'medium',
          created_at: new Date().toISOString()
        };
        
        // Launch the interactive tutorial immediately
        setTimeout(() => {
          handleTutorialStart(tutorialData);
        }, 1000); // Small delay to show success message first
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('🎓 [useTutorials] Tutorial creation failed:', errorData);
        
        // Update the loading message with error
        setMessages(prev => prev.map(msg => 
          msg.isLoading ? {
            ...msg,
            content: `❌ **Tutorial Creation Failed**\n\nSorry, there was an issue creating your tutorial: ${errorData.message || 'Please try again.'}`,
            isLoading: false,
            isError: true
          } : msg
        ));
      }
    } catch (error) {
      console.error('🎓 [useTutorials] Tutorial creation error:', error);
      
      // Update the loading message with network error
      setMessages(prev => prev.map(msg => 
        msg.isLoading ? {
          ...msg,
          content: `❌ **Network Error**\n\nUnable to connect to the tutorial service. Please check your connection and try again.`,
          isLoading: false,
          isError: true
        } : msg
      ));
    }
  };

  return {
    // State
    isTutorialModeActive,
    activeTutorialData,
    tutorialTransitionState,
    isAvatarJourneyActive,
    
    // Functions
    loadTutorialFromStorage,
    reopenLastTutorial,
    handleTutorialStart,
    handleTutorialClose,
    handleAvatarJourneyComplete,
    handleDirectTutorialCreation,
    handleTutorialCreation
  };
};

export default useTutorials;
